import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  QaiInnerWrapComponent,
  QaiProductTileComponent,
  QaiItemGridComponent,
  QaiButtonComponent,
  QaiSlotDirective,
  QaiLoadingComponent,
  QaiEmptyComponent,
  QaiGlyphComponent,
} from '@qai/ui';
import { PersonalizedPracticeService } from 'src/app/services/personalized-practice/personalized-practice.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { IPersonalPracticeSim } from 'src/app/interfaces/personalized-practice/ipersonalized-practice-simulation';
import {
  getAccountNameByUuid
} from 'src/app/utils/local-storage.util';

@Component({
  selector: 'app-personalized-product-list',
  templateUrl: './personalized-product-list.component.html',
  styleUrls: ['./personalized-product-list.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'personalizedPractice',
    },
  ],
  imports: [
    CommonModule,
    TranslocoModule,
    QaiInnerWrapComponent,
    QaiItemGridComponent,
    QaiProductTileComponent,
    QaiButtonComponent,
    QaiSlotDirective,
    QaiLoadingComponent,
    QaiEmptyComponent,
    QaiGlyphComponent,
  ],
})
export class PersonalizedProductListComponent implements OnInit {
  personalSimulations$!: Observable<IPersonalPracticeSim[]>;
  noProductTitle: string = '';
  noProductDescription: string = '';
  accountUuid: string = '';
  accountName: string = '';

  constructor(
    private personalizedPracticeService: PersonalizedPracticeService,
    private amplitudeService: AmplitudeService,
    private router: Router,
    private translationService: TranslationService
  ) {
    this.noProductTitle = this.translationService.translate(
      'NO_PRODUCTS_TITLE',
      'personalizedPractice'
    );
    this.noProductDescription = this.translationService.translate(
      'NO_PRODUCTS_DESCRIPTION',
      'personalizedPractice'
    );
  }

  ngOnInit(): void {
    this.accountUuid = localStorage.getItem('current_account_uuid') || '';
    this.accountName = getAccountNameByUuid(this.accountUuid);
    this.loadPersonalizedPracticeSimulations();
  }

  loadPersonalizedPracticeSimulations(): void {
    this.personalSimulations$ = this.personalizedPracticeService
      .getPersonalizedPracticeSimulations()
      .pipe(
        map(simulations => this.enrichSimulations(simulations)),
        tap({
          error: (err) => console.error('Error loading simulations:', err)
        }),
        catchError(() => of([]))
      );
  }

  enrichSimulations(sims: IPersonalPracticeSim[]): IPersonalPracticeSim[] {
    return sims.map(sim => {
      const ribbonIcon = sim.isActive ? 'stars' : 'lock';
      const ribbonKind = sim.isActive ? 'primary' : 'disabled';
      const buttonKey = sim.isActive ? 'VIEW_SIMULATION' : 'VIEW_SESSIONS';
      const sessionKey = sim.sessionsCount === 1 ? 'TOTAL_SESSION' : 'TOTAL_SESSIONS';

      return {
        ...sim,
        ribbonIcon,
        ribbonKind,
        buttonText: this.translationService.translate(buttonKey, 'personalizedPractice'),
        description: `${sim.sessionsCount} ${this.translationService.translate(sessionKey, 'personalizedPractice')}`,
      };
    });
  }

  viewSession(simulation: IPersonalPracticeSim): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.PERSONALIZED_PRACTICE_CLICK_PRODUCT,
      {
        product_name: simulation.moduleName,
        account_name: this.accountName,
        product_status: simulation.isActive ? 'Active' : 'Past Due',
      }
    );
    this.router.navigate(['/personalized-practice/details', simulation.moduleUuid]);
  }
}
